import { useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';
import { Bold, Italic, Underline, Link as LinkIcon, List, Eraser } from 'lucide-react';

const SANITIZE_CONFIG = {
  ALLOWED_TAGS: ['b', 'strong', 'i', 'em', 'u', 'a', 'p', 'br', 'ul', 'ol', 'li', 'span', 'div'],
  ALLOWED_ATTR: ['href', 'target', 'rel', 'style'],
};

/**
 * Lightweight contentEditable rich-text editor with a tiny toolbar.
 * Output is sanitized HTML (stored in `value`, emitted via `onChange`).
 *
 * Supports: B / I / U, bullet list, link insert, clear formatting.
 * Placeholders like {recipient_name} are preserved verbatim — they are
 * replaced server-side at send time.
 */
export function RichTextEditor({
  value = '',
  onChange,
  placeholder = '',
  testId,
  rows = 8,
}) {
  const editorRef = useRef(null);

  // Keep editor DOM in sync with prop value when it changes externally
  useEffect(() => {
    if (!editorRef.current) return;
    const sanitized = DOMPurify.sanitize(value || '', SANITIZE_CONFIG);
    if (editorRef.current.innerHTML !== sanitized) {
      editorRef.current.innerHTML = sanitized;
    }
  }, [value]);

  const exec = (command, arg) => {
    // execCommand is officially deprecated but still ships in every
    // browser; keeping the editor dependency-free is worth the tradeoff.
    document.execCommand(command, false, arg);
    handleInput();
    editorRef.current?.focus();
  };

  const handleInput = () => {
    const html = editorRef.current?.innerHTML || '';
    const sanitized = DOMPurify.sanitize(html, SANITIZE_CONFIG);
    onChange?.(sanitized);
  };

  const insertLink = () => {
    const url = window.prompt('Enter URL (https://…)');
    if (!url) return;
    if (!/^https?:\/\//i.test(url)) {
      window.alert('Please include https:// at the start');
      return;
    }
    exec('createLink', url);
    const anchors = editorRef.current?.querySelectorAll('a');
    anchors?.forEach((a) => {
      if (a.href === url) {
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
      }
    });
    handleInput();
  };

  const ToolBtn = ({ icon: Icon, label, onClick, testId: btnTestId }) => (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      data-testid={btnTestId}
      className="p-2 rounded-md hover:bg-slate-100 active:bg-slate-200 text-slate-600"
    >
      <Icon className="w-4 h-4" />
    </button>
  );

  return (
    <div className="rich-text-editor border border-slate-200 rounded-lg bg-white overflow-hidden" data-testid={testId}>
      <div className="flex items-center gap-1 border-b border-slate-200 bg-slate-50 px-2 py-1.5">
        <ToolBtn icon={Bold} label="Bold" onClick={() => exec('bold')} testId={testId ? `${testId}-bold` : undefined} />
        <ToolBtn icon={Italic} label="Italic" onClick={() => exec('italic')} testId={testId ? `${testId}-italic` : undefined} />
        <ToolBtn icon={Underline} label="Underline" onClick={() => exec('underline')} testId={testId ? `${testId}-underline` : undefined} />
        <span className="w-px h-5 bg-slate-200 mx-1" />
        <ToolBtn icon={List} label="Bullet list" onClick={() => exec('insertUnorderedList')} />
        <ToolBtn icon={LinkIcon} label="Insert link" onClick={insertLink} testId={testId ? `${testId}-link` : undefined} />
        <span className="w-px h-5 bg-slate-200 mx-1" />
        <ToolBtn icon={Eraser} label="Clear formatting" onClick={() => exec('removeFormat')} />
      </div>
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onBlur={handleInput}
        suppressContentEditableWarning
        className="rich-text-editor__content px-3 py-2 outline-none text-sm prose prose-sm max-w-none"
        data-placeholder={placeholder}
        style={{ minHeight: `${rows * 1.5}rem` }}
        data-testid={testId ? `${testId}-editable` : undefined}
      />
    </div>
  );
}

export default RichTextEditor;
